import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function ShippingPolicyPage() {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen pb-20 transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-300' : 'bg-white text-gray-700'}`}>
      <SEO
        {...pageSeoMeta.policyShipping}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Shipping Policy', url: '/policies/shipping' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Shipping Policy' },
      ]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-8 text-sm font-medium">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`text-3xl sm:text-4xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Shipping Policy</h1>
          <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Last updated: March 2026</p>

          <div className="space-y-8 leading-relaxed">
            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Shipping Coverage</h2>
              <p>We ship to <strong>all major cities and towns across Country</strong>. Orders are dispatched from our warehouse and delivered via trusted courier partners.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Express Airway Delivery</h2>
              <p>All orders are shipped via <strong>express delivery airway</strong>, ensuring faster transit times across Country. Your order is dispatched by air freight through our trusted courier partners for the quickest possible delivery.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Processing Time</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Orders are processed within <strong>1 business day</strong> after payment confirmation.</li>
                <li>Orders placed on weekends or holidays will be processed on the next business day.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Delivery Time</h2>
              <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <th className="text-left py-2 font-semibold">Region</th>
                      <th className="text-left py-2 font-semibold">Estimated Delivery</th>
                    </tr>
                  </thead>
                  <tbody className="space-y-2">
                    <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <td className="py-2">Metro Cities (Delhi, Mumbai, Bangalore, etc.)</td>
                      <td className="py-2">1-2 business days</td>
                    </tr>
                    <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <td className="py-2">Tier 2 Cities</td>
                      <td className="py-2">2-3 business days</td>
                    </tr>
                    <tr>
                      <td className="py-2">Remote / Rural Areas</td>
                      <td className="py-2">3-5 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Shipping Charges</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Free shipping</strong> on all orders above ₹5,000.</li>
                <li>A flat shipping fee of <strong>₹199</strong> applies for orders below ₹5,000.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Order Tracking</h2>
              <p>Once your order is dispatched, you will receive a tracking number via email/SMS. You can use it to track your shipment in real-time.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Discreet Packaging</h2>
              <p>All orders are shipped in <strong>plain, unlabeled packaging</strong> for your privacy. No product branding is visible on the outside of the box.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact Us</h2>
              <p>For shipping queries, email <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a>.</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
